import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useDashboardLayoutStore } from '../stores/dashboardLayoutStore'
import { useInventoryDashboardLayoutStore } from '../stores/inventoryDashboardLayoutStore'
import { useThemeStore, THEMES } from '../stores/themeStore'
import { useUiStore } from '../stores/uiStore'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE } from '../lib/api'
import '../styles/ui.css'

export function AccountMenu() {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()
    const { user, isImpersonating } = useAuthStore()
    const clearAuth = useAuthStore(s => s.clearAuth)
    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const setColorMode = useThemeStore(s => s.setColorMode)
    const effectiveTheme = useThemeStore(s => s.theme ?? s.siteDefaultTheme)
    const setTheme = useThemeStore(s => s.setTheme)
    const { forcedLayout, setForcedLayout } = useUiStore()
    const queryClient = useQueryClient()
    const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'HeadOffice'

    const handleThemeRotate = async () => {
        const currentIndex = THEMES.findIndex(t => t.name === effectiveTheme)
        const nextIndex = (currentIndex + 1) % THEMES.length
        const newTheme = THEMES[nextIndex].name
        setTheme(newTheme)

        const token = useAuthStore.getState().accessToken
        if (token && !isImpersonating) {
            try {
                await fetch(API_BASE + '/api/me/preferences', {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ preferredTheme: newTheme, preferredColorMode: colorMode })
                })
            } catch { /* ignore */ }
        }
    }

    const handleColorModeToggle = async () => {
        const newMode = colorMode === 'dark' ? 'light' : 'dark'
        setColorMode(newMode)

        const token = useAuthStore.getState().accessToken
        if (token && !isImpersonating) {
            try {
                await fetch(API_BASE + '/api/me/preferences', {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ preferredTheme: effectiveTheme, preferredColorMode: newMode })
                })
            } catch { /* ignore */ }
        }
    }

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const handleLogout = async () => {
        setOpen(false)
        try {
            const token = useAuthStore.getState().accessToken
            await fetch(API_BASE + '/api/auth/logout', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            })
        } catch { /* logout locally regardless */ }
        // Wipe all user-specific dashboard state before clearing auth,
        // so the next user that logs in starts with a clean slate.
        useDashboardLayoutStore.getState().clearUserState()
        useInventoryDashboardLayoutStore.getState().clearUserState()
        queryClient.clear()
        clearAuth()
        navigate('/login')
    }

    const initials = user?.displayName
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase() ?? '?'

    return (
        <div className="account-menu-wrapper" ref={ref}>
            {/* Trigger */}
            <button
                className={`account-trigger${open ? ' open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                aria-haspopup="true"
                aria-label="Account menu"
            >
                {user?.avatarUrl ? (
                    <img
                        src={user.avatarUrl}
                        alt={user.displayName}
                        className="user-avatar"
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <div className="user-avatar-fallback">{initials}</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span className="user-name">{user?.displayName}</span>
                    <span className="user-role" style={{ fontSize: '10px' }}>{user?.role}</span>
                </div>
                <span className="caret">▼</span>
            </button>

            {/* Dropdown */}
            {open && (
                <div className="account-dropdown" role="menu">
                    {/* User info header */}
                    <div className="dropdown-user-info">
                        {user?.avatarUrl ? (
                            <img
                                src={user.avatarUrl}
                                alt={user.displayName}
                                className="user-avatar"
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <div className="user-avatar-fallback">{initials}</div>
                        )}
                        <div className="dropdown-user-details">
                            <div className="dropdown-user-name">{user?.displayName}</div>
                            <div className="dropdown-user-email">{user?.email}</div>
                        </div>
                    </div>

                    {/* Navigation items */}
                    <div className="dropdown-section">
                        {!isImpersonating && (
                            <button
                                className="dropdown-item"
                                role="menuitem"
                                onClick={() => { setOpen(false); navigate('/profile') }}
                            >
                                <span className="dropdown-item-icon">👤</span>
                                My Profile
                            </button>
                        )}
                        <button
                            className="dropdown-item"
                            role="menuitem"
                            onClick={() => { setOpen(false); navigate('/my-stores') }}
                        >
                            <span className="dropdown-item-icon">🏪</span>
                            My Store Access
                        </button>
                    </div>

                    {/* Theme toggle */}
                    <div className="dropdown-section">
                        <button
                            className="dropdown-item"
                            role="menuitem"
                            onClick={handleColorModeToggle}
                        >
                            <span className="dropdown-item-icon">{colorMode === 'dark' ? '🌙' : '☀️'}</span>
                            <span style={{ flex: 1 }}>Dark Mode</span>
                            <span className={`theme-toggle-pill ${colorMode === 'dark' ? 'light' : ''}`}>
                                <span className="theme-toggle-knob" />
                            </span>
                        </button>
                        <button
                            className="dropdown-item"
                            role="menuitem"
                            onClick={handleThemeRotate}
                        >
                            <span className="dropdown-item-icon">🎨</span>
                            <span style={{ flex: 1 }}>Theme: {THEMES.find(t => t.name === effectiveTheme)?.label || 'Theme'}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '14px', color: 'var(--color-text-tertiary)' }}>↻</span>
                        </button>
                    </div>

                    {/* Layout Override */}
                    {isAdmin && (
                        <div className="dropdown-section">
                            <div style={{ padding: '6px 16px', fontSize: '10px', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', letterSpacing: '0.05em', fontWeight: 600 }}>Layout Override</div>
                            <button
                                className={`dropdown-item ${forcedLayout === 'auto' ? 'active' : ''}`}
                                onClick={() => { setForcedLayout('auto'); setOpen(false) }}
                            >
                                <span className="dropdown-item-icon">⚙️</span>
                                Auto (Default)
                                {forcedLayout === 'auto' && <span style={{ marginLeft: 'auto', color: 'var(--color-primary)' }}>✓</span>}
                            </button>
                            <button
                                className={`dropdown-item ${forcedLayout === 'desktop' ? 'active' : ''}`}
                                onClick={() => { setForcedLayout('desktop'); setOpen(false) }}
                            >
                                <span className="dropdown-item-icon">💻</span>
                                Force Desktop
                                {forcedLayout === 'desktop' && <span style={{ marginLeft: 'auto', color: 'var(--color-primary)' }}>✓</span>}
                            </button>
                            <button
                                className={`dropdown-item ${forcedLayout === 'mobile' ? 'active' : ''}`}
                                onClick={() => { setForcedLayout('mobile'); setOpen(false) }}
                            >
                                <span className="dropdown-item-icon">📱</span>
                                Force Mobile
                                {forcedLayout === 'mobile' && <span style={{ marginLeft: 'auto', color: 'var(--color-primary)' }}>✓</span>}
                            </button>
                        </div>
                    )}

                    {/* Sign out */}
                    <div className="dropdown-section">
                        <button
                            className="dropdown-item danger"
                            role="menuitem"
                            onClick={handleLogout}
                        >
                            <span className="dropdown-item-icon">↩</span>
                            Sign Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
